// ** React Imports
import { useState, useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'

// ** Next Imports

// ** MUI Imports
import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid'
import Card from '@mui/material/Card'
import Tooltip from '@mui/material/Tooltip'
import { styled, createTheme } from '@mui/material/styles'
import MenuItem from '@mui/material/MenuItem'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import MuiTabList from '@mui/lab/TabList'
import LinearProgress from '@mui/material/LinearProgress'

// ** Icon Imports
import Icon from 'src/@core/components/icon'

// ** Styled Components
import CustomHeader from 'src/@core/components/Header'
import {
  Alert,
  AlertTitle,
  AppBar,
  Avatar,
  Badge,
  Button,
  ButtonGroup,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListSubheader,
  Paper,
  Snackbar,
  Tab,
  Toolbar,
  useMediaQuery
} from '@mui/material'
import { TabContext, TabPanel } from '@mui/lab'
import {
  AccountTree,
  AddSharp,
  AddToPhotos,
  LibraryAdd,
  Looks3,
  Looks4,
  LooksOne,
  LooksTwo,
  NewReleases,
  RemoveSharp
} from '@mui/icons-material'

import { beastList, beastSkills, beastSkillsTemplates, dataOptions } from 'src/data/beast'
import HuecoBarraInferior from 'src/@core/components/layout/HuecoBarraInferior'
import { construirEnlace, leerEstadoDeUrl } from 'src/@core/utils/enlace-compartible'
import TablaReferencia from 'src/@core/components/table/TablaReferencia'

/*
  Tabla de referencia renderizada en el servidor. /beast/ servia 636 palabras y
  cero filas sobre 44 KB: los 28 talentos viven dentro de tarjetas que dependen de
  la bestia elegida, asi que el rastreador solo veia los de una.

  Una fila por talento y nivel: es la consulta real ("que da este talento al nivel
  7 y cuanto cuesta"), y es lo que el simulador no deja leer de corrido. El texto
  del efecto lleva un marcador $1 que se sustituye por el valor del nivel, para que
  cada fila diga lo que hace y no una plantilla.
*/
const COLUMNAS_BEAST = [
  { clave: 'talento', encabezado: 'Talent' },
  { clave: 'bestia', encabezado: 'Available to' },
  { clave: 'maxLevel', encabezado: 'Max level', numerica: true },
  { clave: 'total', encabezado: 'Points to max', numerica: true, formato: v => v.toLocaleString('en-US') },
  { clave: 'efecto', encabezado: 'What it does' }
]

const FILAS_BEAST = dataOptions.map(o => ({
  id: o.row + o.col + '-' + o.beast,
  talento: o.description,
  bestia: o.beast === 'all' || o.beast === 'any' ? 'All beasts' : o.beast,
  maxLevel: o.maxCounter ?? 0,
  total: (o.levels ?? []).reduce((a, l) => a + (l.points ?? 0), 0),
  efecto: String(o.statText ?? '').replace(/\$1/g, 'X')
}))

/*
  Dos tablas y no una.

  La primera version ponia una fila por talento y nivel con la frase del efecto
  entera repetida en cada una: 421 filas, 12.706 palabras y 50 KB gzip, que dejaba
  a /beast/ como la pagina mas pesada del sitio por delante de /realms/. La frase
  es la misma en los quince niveles salvo por un numero, asi que eran quince copias
  de un texto para transmitir quince cifras.

  Ahora la frase va una vez por talento, con la X en el hueco del valor, y la tabla
  de niveles lleva solo el numero. Mismo dato, sin la repeticion.
*/
const COLUMNAS_BEAST_NIVEL = [
  { clave: 'talento', encabezado: 'Talent' },
  { clave: 'nivel', encabezado: 'Level', numerica: true },
  { clave: 'valor', encabezado: 'Value of X', numerica: true },
  { clave: 'puntos', encabezado: 'Talent points', numerica: true, formato: v => v.toLocaleString('en-US') },
  { clave: 'acumulado', encabezado: 'Cumulative', numerica: true, formato: v => v.toLocaleString('en-US') }
]

const FILAS_BEAST_NIVEL = dataOptions.flatMap(o => {
  let acumulado = 0

  return (o.levels ?? []).map(l => {
    acumulado += l.points ?? 0

    return {
      id: o.row + o.col + '-' + o.beast + '-' + l.level,
      talento: o.description,
      nivel: l.level,
      valor: Array.isArray(l.stat) ? l.stat.join(' / ') : l.stat,
      puntos: l.points ?? 0,
      acumulado
    }
  })
})


// ** Styled component for the link in the dataTable
const theme = createTheme()

const GreenButton = styled(Button)(({ theme }) => ({
  // Verde oscuro y texto blanco: #E8F1F5 sobre #1B8A6B daba 3,74:1, por debajo de AA.
  backgroundColor: theme.palette.customColors.skyPaletteTertiaryDark,
  color: theme.palette.common.white,
  '&:hover': {
    backgroundColor: theme.palette.customColors.skyPaletteTertiaryDark,
    filter: 'brightness(0.85)'
  }
}))

const RedButton = styled(Button)(({ theme }) => ({
  color: theme.palette.error.contrastText,
  backgroundColor: theme.palette.error.main,
  '&:hover': {
    backgroundColor: theme.palette.error.dark,
    filter: 'brightness(0.85)'
  }
}))

const TabList = styled(MuiTabList)(({ theme }) => ({
  '& .MuiTabs-indicator': {
    display: 'none'
  },
  '& .Mui-selected': {
    backgroundColor: theme.palette.primary.main,
    // contrastText: en modo oscuro el primary es claro y el blanco daba 2,30:1
    color: `${theme.palette.primary.contrastText} !important`
  },
  '& .MuiTab-root': {
    minWidth: 65,
    minHeight: 38,
    paddingTop: theme.spacing(2.5),
    paddingBottom: theme.spacing(2.5),
    borderRadius: theme.shape.borderRadius,
    [theme.breakpoints.up('sm')]: {
      minWidth: 130
    }
  }
}))

// ** Var custom
/*
  Las opciones por defecto se filtran aca, en el modulo, y no dentro del efecto.

  Antes `isLoading` arrancaba en true, asi que el HTML estatico de /beast/ salia
  con un spinner y nada mas; el simulador entero aparecia recien al hidratar.
  Medido con CDP a 412 px: la seccion del simulador pasaba de 192 px a 3.388 px y
  empujaba 3.200 px hacia abajo todo lo que venia despues. Lighthouse lo puntuaba
  como 0,168 de CLS.

  Al ser un valor determinista —solo depende de dataOptions y del bestia por
  defecto— se puede calcular en servidor y cliente por igual, sin desajuste de
  hidratacion. El efecto de mas abajo sigue existiendo para sobreescribirlo con
  lo que haya guardado en localStorage.

  Efecto lateral bienvenido: ahora el simulador tambien esta en el HTML que ve
  Google, no solo la palabra "Loading...".
*/
const opcionesDeBestia = bestia =>
  [...dataOptions].filter(o => o.beast === 'all' || o.beast === 'any' || o.beast === bestia)

const defaultBeastInfo = {
  options: opcionesDeBestia('pegasus'),
  beast: 'pegasus',
  userTalents: 0
}

/*
  Puntos acumulados hasta un nivel. Es la misma cuenta que hace handleFillAll, que
  suma los `points` de cada nivel; aca se corta en el nivel pedido en vez de sumar
  la lista entera.
*/
const nivelATotal = (opcion, nivel) => (opcion.levels ?? []).slice(0, nivel).reduce((acc, l) => acc + l.points, 0)

/*
  Lo que viaja en un enlace compartido: la bestia, los talentos disponibles y el
  nivel elegido en cada talento. Nada mas.

  Guardar el estado entero serian 21,7 KB de JSON —los niveles, los puntos y los
  textos de cada talento, que ya estan en src/data/beast—, o sea 29 KB en base64.
  Esto son unos 200 caracteres.

  La clave de cada talento es fila+columna y no su posicion en el array: si mañana
  se agrega un talento en medio, un enlace viejo sigue apuntando a los mismos.
*/
const aEstadoMinimo = info => ({
  b: info.beast,
  t: info.userTalents || 0,
  o: info.options.reduce((acc, o) => {
    if (o.counter > 0) acc[`${o.row}${o.col}`] = o.counter

    return acc
  }, {})
})

const desdeEstadoMinimo = minimo => {
  if (!minimo || typeof minimo.b !== 'string') return null

  /*
    La bestia se valida contra el catalogo, no contra el resultado de filtrar.
    opcionesDeBestia devuelve tambien los talentos comunes ('all' y 'any'), asi que
    para un nombre inventado no devuelve una lista vacia: devuelve los comunes. Con
    el guardia puesto sobre la longitud, un ?s= con b:"griffin" abria la herramienta
    en un estado a medias —solo los talentos compartidos— en vez de ignorarse.
  */
  if (!beastList.some(b => b.key === minimo.b)) return null

  const opciones = opcionesDeBestia(minimo.b)
  if (!opciones.length) return null

  const elegidos = minimo.o && typeof minimo.o === 'object' ? minimo.o : {}

  return {
    beast: minimo.b,
    userTalents: Number(minimo.t) || 0,
    options: opciones.map(o => {
      // Un enlace manipulado a mano no puede dejar la herramienta en un estado
      // imposible: el nivel se recorta a lo que ese talento admite.
      const pedido = Number(elegidos[`${o.row}${o.col}`]) || 0
      const counter = Math.min(Math.max(pedido, o.minCounter ?? 0), o.maxCounter ?? 0)

      return { ...o, counter, curLevel: counter, curTotalPoints: nivelATotal(o, counter) }
    })
  }
}

function LinearProgressWithLabel(props) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <Box sx={{ width: '100%', mr: 1 }}>
        {/* aria-label: sin nombre accesible, un progressbar no dice de que informa */}
        <LinearProgress
          color='success'
          variant='determinate'
          aria-label='Talent points spent'
          {...props}
        />
      </Box>
      <Box sx={{ minWidth: 35 }}>
        {/*
          Blanco pleno en vez de #ffffffcf: sobre el azul #0278AE de la barra
          inferior, ese blanco al 81% resolvia a #cfe5f0 y daba 3,74:1.
        */}
        <Typography variant='body2' sx={{ color: 'common.white' }}>{`${Math.round(props.value)}%`}</Typography>
      </Box>
    </Box>
  )
}

LinearProgressWithLabel.propTypes = {
  /**
   * The value of the progress indicator for the determinate and buffer variants.
   * Value between 0 and 100.
   */
  value: PropTypes.number.isRequired
}

 
const Beast = () => {
  // ** State
  const [activeTab, setActiveTab] = useState('talents')
  const [isLoading, setIsLoading] = useState(false)
  const hideText = useMediaQuery(theme => theme.breakpoints.down('sm'))
  const [beastInfo, setBeastInfo] = useState(defaultBeastInfo)
  const beastSkillList = beastSkills.beastSkillList
  const [slotLevel, setSlotLevel] = useState(beastSkills.slots)
  const [skillTemplate, setSkillTemplate] = useState('')


  const [snackOpen, setSnackOpen] = useState({ open: false })

  const [totalAvailablePoints, setTotalAvailablePoints] = useState(
    [...dataOptions]
      .filter(o => o.beast === 'all' || o.beast === 'any' || o.beast === beastInfo.beast)
      .map(o => o.levels)
      .map(od => {
        return od.reduce((accumulator, currentValue) => accumulator + currentValue.points, 0)
      })
      .reduce((accumulator, currentValue) => accumulator + currentValue, 0)
  )

  const totalPoints = beastInfo.options.reduce(
    (accumulator, currentValue) => accumulator + currentValue.curTotalPoints,
    0
  )

  // ** Hooks
  useEffect(() => {
    /*
      El enlace compartido gana sobre lo guardado, y a proposito: si alguien abre un
      enlace de su alianza, quiere ver esa configuracion, no la suya. Lo suyo sigue
      en localStorage y vuelve al entrar por /beast/ sin parametros.

      Lo guardado no se pisa. Para quedarse con lo que llego por el enlace hay que
      pulsar SAVE, que es lo mismo que para cualquier otro cambio.
    */
    const compartido = desdeEstadoMinimo(leerEstadoDeUrl())
    if (compartido) {
      setBeastInfo(compartido)

      return
    }

    // El estado por defecto ya viene resuelto: aca solo se restaura lo guardado.
    let saved
    try {
      saved = JSON.parse(window.localStorage.getItem('beastInfo'))
    } catch (e) {
      saved = null
    }
    if (saved) setBeastInfo(saved)
  }, [])

  const handleShare = async () => {
    const enlace = construirEnlace(aEstadoMinimo(beastInfo))
    if (!enlace) return setSnackOpen({ open: 'Could not build the link.', type: 'error', time: 4000 })

    try {
      await navigator.clipboard.writeText(enlace)

      return setSnackOpen({ open: 'Link copied — paste it anywhere.', type: 'success', time: 4000 })
    } catch (e) {
      /*
        El portapapeles necesita https y, en algunos navegadores, un gesto reciente.
        Si lo rechaza, la URL se pone en la barra de direcciones: no queda tan a mano
        pero el enlace existe y se puede copiar desde ahi. Peor seria decir "copiado"
        sin haber copiado nada.
      */
      window.history.replaceState(null, '', enlace)

      return setSnackOpen({ open: 'Link is now in the address bar — copy it from there.', type: 'info', time: 6000 })
    }
  }

  /*
    `progress` es un valor derivado, no estado.

    Estaba en useState y se escribia desde un useEffect, asi que cada clic en
    + o - provocaba dos renders completos del arbol en vez de uno: el del
    cambio de opciones y, acto seguido, el de setProgress. Con 19 tarjetas en
    beast y 31 en college, eso es el doble de trabajo de React por cada
    interaccion, y es lo que mide INP.

    useMemo lo calcula durante el render que ya estaba ocurriendo. Efecto
    lateral: la barra ya nace con su valor real en vez de saltar desde 0.
  */
  const progress = useMemo(() => {
    if (beastInfo.userTalents > 0) {
      return 100 - ((beastInfo.userTalents - totalPoints) / beastInfo.userTalents) * 100
    }

    return 100 - ((totalAvailablePoints - totalPoints) / totalAvailablePoints) * 100
  }, [totalPoints, totalAvailablePoints, beastInfo.userTalents])

  const handleChange = (event, value) => {
    setActiveTab(value)
  }

  const handleSaveData = e => {
    localStorage.setItem('beastInfo', JSON.stringify(beastInfo))

    return setSnackOpen({ open: `Information Stored in the browser!`, type: 'success', time: 3000 })
  }

  const snackClose = (event, reason) => {
    if (reason === 'clickaway') {
      return
    }
    setSnackOpen({ open: false })
  }

  // Beast Talents
  const handleBeastChange = e => {
    const newBeast = e.target.value
    const newOptions = [...dataOptions].filter(o => o.beast === 'all' || o.beast === 'any' || o.beast === newBeast)
    setBeastInfo({ ...beastInfo, options: newOptions, beast: newBeast })
  }

  const handlePrerequisiteFill = (row, col) => {
    let newOptionArray = [...beastInfo.options]
    let expArrayFiltered
    let dependencyArray = []

    const optionObj = beastInfo.options?.find(o => o.row === row && o.col === col)
    const reqArray = optionObj.dependency
    const reqArrayB = optionObj?.dependencyB

    if (reqArray?.length > 0) {
      reqArray.forEach(d => {
        const checkDependency = beastInfo.options?.find(opt => opt.row === d.row && opt.col === d.col)
        if (d?.minLevel > checkDependency?.curLevel) {
          // Verify dependency
          dependencyArray.push({
            description: checkDependency?.description,
            minLevel: d?.minLevel,
            counter: d?.minLevel
          })

          if (d?.minLevel > 0) {
            expArrayFiltered = checkDependency.levels?.filter(obj => obj.level > 0 && obj.level <= d?.minLevel)
          }

          const newPointsNeeded = expArrayFiltered?.reduce(
            (accumulator, currentValue) => accumulator + currentValue.points,
            0
          )

          newOptionArray = newOptionArray.map(obj =>
            obj.row === checkDependency.row && obj.col === checkDependency.col
              ? { ...checkDependency, counter: d?.minLevel, curLevel: d?.minLevel, curTotalPoints: newPointsNeeded }
              : obj
          )
        }
      })
    }

    if (reqArrayB?.length > 0 && optionObj?.curLevel >= optionObj?.dependencyBlevel) {
      reqArrayB.forEach(d => {
        const checkDependency = beastInfo.options?.find(opt => opt.row === d.row && opt.col === d.col)
        if (d?.minLevel > checkDependency?.curLevel) {
          // Verify dependency
          dependencyArray.push({
            description: checkDependency?.description,
            minLevel: d?.minLevel,
            counter: d?.minLevel
          })

          if (d?.minLevel > 0) {
            expArrayFiltered = checkDependency.levels?.filter(obj => obj.level > 0 && obj.level <= d?.minLevel)
          }

          const newPointsNeeded = expArrayFiltered?.reduce(
            (accumulator, currentValue) => accumulator + currentValue.points,
            0
          )

          newOptionArray = newOptionArray.map(obj =>
            obj.row === checkDependency.row && obj.col === checkDependency.col
              ? { ...checkDependency, counter: d?.minLevel, curLevel: d?.minLevel, curTotalPoints: newPointsNeeded }
              : obj
          )
        }
      })
    }

    const checkPointsLimit = [...newOptionArray]
      .filter(o => o.beast === 'all' || o.beast === 'any' || o.beast === beastInfo.beast)
      .reduce((accumulator, currentValue) => accumulator + currentValue.curTotalPoints, 0)

    if (checkPointsLimit > beastInfo.userTalents && beastInfo.userTalents > 0)
      return setSnackOpen({
        open: `You dont have enough BEAST TALENTS points available. You need ${checkPointsLimit?.toLocaleString()} and you have a limit of ${beastInfo.userTalents?.toLocaleString()} `,
        type: 'warning',
        time: 3000
      })

    setBeastInfo(prevState => ({ ...prevState, options: newOptionArray }))

    setSnackOpen({ open: `Prerequisites loaded.`, type: 'success', time: 3000 })
  }

  const handleIncrease = (row, col, incAll = false) => {
    const oldObject = beastInfo.options.find(r => r.row === row && r.col === col)
    if (oldObject?.counter === oldObject?.maxCounter) return

    let newTotalPoints = 0
    let newState

    const newCounter = oldObject.counter === oldObject.maxCounter ? oldObject.maxCounter : oldObject.counter + 1
    const dependencyObj = []

    let dependencyMsj = '',
      dependencyMet = false

    if (oldObject?.dependency?.length > 0) {
      oldObject.dependency.forEach(d => {
        const checkDependency = beastInfo.options.find(o => o.row === d.row && o.col === d.col)
        if (d?.minLevel > checkDependency?.counter) {
          dependencyObj.push({
            row: checkDependency?.row,
            col: checkDependency?.col,
            counter: checkDependency?.counter,
            description: checkDependency?.description,
            minLevel: d?.minLevel
          })
        }
      })

      if (dependencyObj?.length > 0) {
        dependencyMet = false
        dependencyMsj = dependencyObj
          ?.map(d => {
            return `Needs ${d?.description} to reach lv${d?.minLevel}. (${d?.counter}/${d?.minLevel}). `
          })
          .join('')
      } else {
        dependencyMet = true
      }
    }

    if (oldObject?.dependencyB?.length > 0 && newCounter > oldObject?.dependencyBlevel) {
      oldObject.dependencyB.forEach(d => {
        const checkDependency = beastInfo.options.find(o => o.row === d.row && o.col === d.col)
        if (d?.minLevel > checkDependency?.counter) {
          dependencyObj.push({
            row: checkDependency?.row,
            col: checkDependency?.col,
            counter: checkDependency?.counter,
            description: checkDependency?.description,
            minLevel: d?.minLevel
          })
        }
      })

      if (dependencyObj?.length > 0) {
        dependencyMet = false
        dependencyMsj = dependencyObj
          ?.map(d => {
            return `Needs ${d?.description} to reach lv${d?.minLevel}. (${d?.counter}/${d?.minLevel}).`
          })
          .join('')
      } else {
        dependencyMet = true
      }
    }

    if (oldObject.dependency === 0 && typeof oldObject.dependency === 'number') dependencyMet = true

    if (!dependencyMet) {
      const message = dependencyMsj
      dependencyMsj = ''

      // `The conditions to upgrade the Talent aren't met: `
      return setSnackOpen({ open: message, type: 'warning', time: 6000 })
    }

    const oldTotalPoints = oldObject?.curTotalPoints

    // Fill skill at maxCounter
    if (incAll) {
      if (row === 'I' && col === 1 && oldObject?.dependencyBlevel > oldObject?.counter) {
        newTotalPoints = oldObject?.levels
          ?.filter(l => l.level > oldObject.counter && l.level < oldObject?.dependencyBlevel)
          .reduce((accumulator, currentValue) => accumulator + currentValue.points, 0)
        newState = beastInfo.options?.map(obj =>
          obj.row === row && obj.col === col
            ? {
                ...obj,
                counter: oldObject?.dependencyBlevel,
                curLevel: oldObject?.dependencyBlevel,
                curTotalPoints: oldTotalPoints + newTotalPoints
              }
            : obj
        )
      } else {
        newTotalPoints = oldObject?.levels
          ?.filter(l => l.level > oldObject.counter)
          .reduce((accumulator, currentValue) => accumulator + currentValue.points, 0)
        newState = beastInfo.options?.map(obj =>
          obj.row === row && obj.col === col
            ? {
                ...obj,
                counter: oldObject.maxCounter,
                curLevel: oldObject.maxCounter,
                curTotalPoints: oldTotalPoints + newTotalPoints
              }
            : obj
        )
      }
    } else {
      newTotalPoints = oldObject?.levels?.find(l => l.level === newCounter)?.points
      newState = beastInfo.options?.map(obj =>
        obj.row === row && obj.col === col
          ? { ...obj, counter: newCounter, curLevel: newCounter, curTotalPoints: oldTotalPoints + newTotalPoints }
          : obj
      )
    }

    const checkPointsLimit = [...newState]
      .filter(o => o.beast === 'all' || o.beast === 'any' || o.beast === beastInfo.beast)
      .reduce((accumulator, currentValue) => accumulator + currentValue.curTotalPoints, 0)

    if (checkPointsLimit > beastInfo.userTalents && beastInfo.userTalents > 0)
      return setSnackOpen({
        open: `You dont have enough BEAST TALENTS points available. You need ${checkPointsLimit?.toLocaleString()} and you have a limit of ${beastInfo.userTalents?.toLocaleString()} `,
        type: 'warning',
        time: 3000
      })

    setBeastInfo(prevState => ({ ...prevState, options: newState }))
  }

  const handleDrecrease = (row, col) => {
    const oldObject = beastInfo.options.find(r => r.row === row && r.col === col)
    if (oldObject?.counter === oldObject?.minCounter) return
    const newCounter = oldObject.counter

    const oldTotalPoints = oldObject?.curTotalPoints
    const newTotalPoints = oldObject?.levels?.find(l => l.level === newCounter)?.points

    const newState = beastInfo.options?.map(obj =>
      obj.row === row && obj.col === col
        ? { ...obj, counter: newCounter - 1, curLevel: newCounter - 1, curTotalPoints: oldTotalPoints - newTotalPoints }
        : obj
    )
    setBeastInfo(prevState => ({ ...prevState, options: newState }))
  }

  const handleFillAll = e => {
    const defaultBeast = beastInfo.beast ?? defaultBeastInfo.beast

    const newState = [...beastInfo.options]?.map(obj => {
      return {
        ...obj,
        counter: obj.maxCounter,
        curLevel: obj.maxCounter,
        curTotalPoints: obj.levels.reduce((accumulator, currentValue) => accumulator + currentValue.points, 0)
      }
    })

    const totalPoints = newState
      .filter(o => o.beast === 'all' || o.beast === 'any' || o.beast === defaultBeast)
      .map(o => o.levels)
      .map(od => {
        return od.reduce((accumulator, currentValue) => accumulator + currentValue.points, 0)
      })
      .reduce((accumulator, currentValue) => accumulator + currentValue, 0)

    const checkPointsLimit = totalPoints

    if (checkPointsLimit > beastInfo.userTalents && beastInfo.userTalents > 0)
      return setSnackOpen({
        open: `You dont have enough BEAST TALENTS points available. You need ${checkPointsLimit?.toLocaleString()} and you have a limit of ${beastInfo.userTalents?.toLocaleString()} `,
        type: 'warning',
        time: 3000
      })

    setTotalAvailablePoints(totalPoints)
    setBeastInfo(prevState => ({ ...prevState, options: newState }))
  }

  const handleClearAll = e => {
    setBeastInfo(prevState => ({
      ...prevState,
      options: [...dataOptions].filter(o => o.beast === 'all' || o.beast === 'any' || o.beast === prevState.beast)
    }))
  }

  const handleRemoveItems = e => {
    setBeastInfo({ ...defaultBeastInfo })
    localStorage.removeItem('beastInfo')

    return setSnackOpen({ open: `Information Cleared from the browser.`, type: 'error' })
  }

  // Beast Skills
  const handleBeastSkillChange = (e, slot) => {
    const newBeastSkill = e.target.value
    const oldObject = slotLevel?.find(r => r.key === slot)
    const alreadyPicked = slotLevel?.find(s => s.skill.key === newBeastSkill)

    if (alreadyPicked) {
      const message = `This skill was already picked at SLOT #${alreadyPicked.key}.`

      return setSnackOpen({ open: message, type: 'warning', time: 6000 })
    }

    if (oldObject) {
      const newSkill = beastSkillList.find(bs => bs.key === newBeastSkill)
      const newState = slotLevel?.map(obj => (obj.key === slot ? { ...obj, skill: newSkill } : obj))
      setSlotLevel(newState)
    }
  }

  const handleBeastSkillChangeTemplate = e => {
    const skillTemplate = +e.target.value
    const newTemplate = beastSkillsTemplates.find(t => t.k === skillTemplate)

    if (!newTemplate) {
      return snackOpen({ open: 'Template not exists.', type: 'error', time: 3000 })
    }

    const newSlotLevel = [...slotLevel]

    slotLevel.forEach((slot, i) => {
      const skillKey = newTemplate?.skills[i]
      const newSkill = beastSkillList.find(bs => bs.key === skillKey)
      const maxLevel = newSlotLevel[i].maxLevel

      const newExpNeeded = beastSkills.skillLevel
        .filter(r => r.level <= maxLevel)
        .reduce((accumulator, currentValue) => accumulator + currentValue.exp, 0)

      newSlotLevel[i].skill = newSkill
      newSlotLevel[i].level = maxLevel
      newSlotLevel[i].expNeeded = newExpNeeded
    })

    setSlotLevel(newSlotLevel)
    setSkillTemplate(skillTemplate)
  }

  const handleDrecreaseBeastSkillChange = slot => {
    const oldObject = slotLevel?.find(r => r.key === slot)
    if (oldObject?.level === oldObject?.minLevel) return
    const newCounter = oldObject.level === oldObject.minLevel ? oldObject.minLevel : oldObject.level - 1
    const levelExp = beastSkills.skillLevel.find(s => s.level === oldObject?.level)?.exp
    const newExpNeeded = oldObject.expNeeded - levelExp

    const newState = slotLevel?.map(obj =>
      obj.key === slot ? { ...obj, level: newCounter, expNeeded: newExpNeeded } : obj
    )
    setSlotLevel(newState)
  }

  const handleIncreaseBeastSkillChange = slot => {
    const oldObject = slotLevel?.find(r => r.key === slot)
    if (oldObject?.level === oldObject?.maxLevel) return
    const newCounter = oldObject.level === oldObject.maxLevel ? oldObject.maxLevel : oldObject.level + 1
    const levelExp = beastSkills.skillLevel.find(s => s.level === newCounter)?.exp
    const newExpNeeded = oldObject.expNeeded + levelExp

    const newState = slotLevel?.map(obj =>
      obj.key === slot ? { ...obj, level: newCounter, expNeeded: newExpNeeded } : obj
    )
    setSlotLevel(newState)
  }

  const handleIncreaseBeastSkillAll = (slot, level) => {
    if (!slot || !level) return

    const newCounter = +level

    const newExpNeeded = beastSkills.skillLevel
      .filter(r => r.level <= level)
      .reduce((accumulator, currentValue) => accumulator + currentValue.exp, 0)

    const newState = slotLevel?.map(obj =>
      obj.key === slot ? { ...obj, level: newCounter, expNeeded: newExpNeeded } : obj
    )
    setSlotLevel(newState)

    return setSnackOpen({ open: 'Max level set.', type: 'success', time: 3000 })
  }

  const tabContentList = {
    talents: (
      <>
        <Card>
          <Box sx={{ flexGrow: 1 }}>
            <Snackbar
              open={snackOpen.open ? true : false}
              autoHideDuration={snackOpen.time ?? 7000}
              onClose={snackClose}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
              <Alert variant='filled' onClose={snackClose} severity={snackOpen.type}>
                <AlertTitle>{snackOpen.type === 'success' ? 'Done!' : 'Info:'}</AlertTitle>
                {snackOpen.open}
              </Alert>
            </Snackbar>
            <Grid container spacing={2} sx={{ padding: '0.5em' }}>
              <Grid size={{ xs: 12 }} sx={{ display: 'flex' }}>
                <Paper elevation={1} sx={{ width: '100%' }}>
                  <Typography
                    align='center'
                    variant='h6' component='p'
                    color='primary'
                    sx={{ paddingTop: '0.5em', paddingBottom: '0.5em' }}
                  >
                    BEAST TALENTS SIMULATOR
                  </Typography>
                  <Divider light>{<Typography variant='caption'>USER CONFIGURATION</Typography>}</Divider>

                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      justifyContent: 'center',
                      marginTop: '10px',
                      padding: '0.5em',
                      gap: '1rem',
                      alignItems: 'center'
                    }}
                  >
                    <GreenButton
                      variant='contained'
                      startIcon={<Icon icon='ic:baseline-save' />}
                      onClick={handleSaveData}
                    >
                      SAVE
                    </GreenButton>
                    <RedButton
                      edge='start'
                      variant='contained'
                      startIcon={<Icon icon='ic:baseline-delete' />}
                      onClick={handleRemoveItems}
                    >
                      DELETE
                    </RedButton>
                    <Button
                      variant='outlined'
                      startIcon={<Icon icon='ic:baseline-link' />}
                      onClick={handleShare}
                    >
                      SHARE
                    </Button>
                  </Box>
                  <Divider />
                  <Box
                    elevation={4}
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between',
                      padding: '0.6em'
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        flexWrap: 'wrap',
                        justifyContent: 'center',
                        alignItems: 'center'
                      }}
                    >
                      <Box sx={{ display: 'flex', flexDirection: 'row', mt: 3 }}>
                        <TextField
                          size='small'
                          align='center'
                          select
                          label='Beast:'
                          id='beast-select'
                          /*
                            value y no defaultValue. Con defaultValue el desplegable
                            se queda con lo que hubiera en el primer render, y el
                            estado real llega despues —desde localStorage o desde un
                            enlace compartido—, asi que nunca se enteraba. Efecto
                            visible sin tocar nada nuevo: se elige Dragon, se guarda,
                            se recarga, y la pagina queda con el avatar del dragon y
                            el desplegable diciendo Pegasus.
                          */
                          value={beastInfo.beast}
                          variant='outlined'
                          type='info'
                          helperText=''
                          onChange={handleBeastChange}
                        >
                          {beastList?.map(option => (
                            <MenuItem key={option.key} value={option.key}>
                              {option.desc}
                            </MenuItem>
                          ))}
                        </TextField>
                        <Avatar sx={{ ml: 3 }} alt={beastInfo.beast} src={`/images/beast/${beastInfo.beast}.webp`} />
                      </Box>
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'row',
                          alignItems: 'center',
                          padding: '0.5em',
                          flexWrap: 'wrap'
                        }}
                      >
                        <Typography variant='body2' color='primary' align='right' sx={{ marginRight: '3px' }}>
                          Set Beast Talents Limit:
                        </Typography>

                        <TextField
                          slotProps={{ htmlInput: { 'aria-label': 'Set Beast Talents Limit' } }}
                          id='talents-limit'
                          size='small'
                          variant='outlined'
                          type='number'
                          sx={{ maxWidth: '150px' }}
                          value={beastInfo.userTalents > 0 ? beastInfo.userTalents : ''}
                          onChange={e => {
                            const value =
                              +e.target.value >= totalAvailablePoints ? totalAvailablePoints : +e.target.value

                            setBeastInfo(prevState => ({
                              ...prevState,
                              userTalents: value
                            }))
                          }}
                        />
                        <Tooltip title={'New option!'} arrow>
                          <NewReleases color='primary' />
                        </Tooltip>
                      </Box>
                    </Box>
                  </Box>
                  <Divider light>{<Typography variant='caption'>TREE</Typography>}</Divider>
                  <Box sx={{ minWidth: `${hideText ? '1px' : '520px'}` }}>
                  {/*
                    `opt` ya es el elemento: este map recorre las propias
                    opciones, asi que buscarlo otra vez por row/col devolvia
                    siempre el mismo objeto. Eran 21 recorridos lineales del
                    array por tarjeta, en cada render, y React vuelve a
                    renderizar todas las tarjetas en cada clic de + o -.
                    Medido a 4x de CPU: 223 ms de script por clic.
                  */}
                    {beastInfo.options.map(opt => {
                      if (
                        opt.type === 1 &&
                        (opt.beast === beastInfo.beast || opt.beast === 'all' || opt.beast === 'any')
                      ) {
                        return (
                          <>
                            {opt.col === 1 && opt.row === 'K' && (
                              <>
                                <div style={{ marginTop: '5px' }} />

                                <Divider>{<Typography variant='caption'>NEW BRANCH</Typography>}</Divider>
                                <Divider light sx={{ marginTop: '5px' }} />
                              </>
                            )}
                            <Grid container>
                              <Grid size={{ xs: 3, sm: 3 }}key={`${opt.row}-col1_empty`}></Grid>
                              <Grid size={{ xs: 6, sm: 6 }}key={`${opt.row}-col1`} sx={{ padding: '0.5rem' }}>
                                <Paper
                                  elevation={12}
                                  sx={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    justifyContent: 'center',
                                    width: `${hideText ? '150px' : '200px'}`
                                  }}
                                  key={`${opt.row}-col1-paper`}
                                >
                                  <Box
                                    sx={{
                                      margin: theme.spacing(1),
                                      minWidth: 90
                                    }}
                                    key={`${opt.row}-col1-paper-box`}
                                  >
                                    <Typography
                                      variant='body2'
                                      color='primary'
                                      align='center'
                                      sx={{ marginBottom: '10px' }}
                                    >
                                      {beastInfo.options?.find(o => o.row === opt.row && o.col === 1)?.description}
                                    </Typography>

                                    <Box
                                      sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        flexGrow: 'revert',
                                        alignContent: 'center',
                                        justifyContent: 'center',
                                        alignItems: 'center'
                                      }}
                                      key={`${opt.row}-col1-paper-box-sub`}
                                    >
                                      <Badge
                                        color={
                                          opt
                                            ?.counter ===
                                          opt
                                            ?.maxCounter
                                            ? 'error'
                                            : 'primary'
                                        }
                                        badgeContent={
                                          opt
                                            ?.curTotalPoints
                                        }
                                        max={50001}
                                      >
                                        <Avatar
                                          alt={`${opt.row}_${opt.col}`}
                                          src={`/images/beast/row${opt.row}_${opt.col}_${
                                            opt.beast === 'any' ? beastInfo.beast : opt.beast
                                          }.webp`}
                                          sx={{
                                            width: theme.spacing(7),
                                            height: theme.spacing(7)
                                          }}
                                        />
                                      </Badge>
                                      <Typography
                                        color={
                                          opt
                                            ?.counter ===
                                          opt
                                            ?.maxCounter
                                            ? 'error'
                                            : 'primary'
                                        }
                                        variant='body2'
                                        sx={{
                                          marginTop: '-12px'
                                        }}
                                      >
                                        {opt?.counter}{' '}
                                        /{' '}
                                        {
                                          opt
                                            ?.maxCounter
                                        }{' '}
                                      </Typography>

                                      {/* MAXED */}
                                      {opt?.counter ===
                                        opt
                                          ?.maxCounter && (
                                        <Typography
                                          color={
                                            opt
                                              ?.counter ===
                                            opt
                                              ?.maxCounter
                                              ? 'error'
                                              : 'primary'
                                          }
                                          variant='body2'
                                          style={{ marginTop: '1px', marginBottom: '-5px' }}
                                        >
                                          {opt
                                            ?.counter ===
                                          opt
                                            ?.maxCounter
                                            ? '(MAXED)'
                                            : ''}
                                        </Typography>
                                      )}

                                      {/* NEXT LEVEL */}
                                      {opt?.counter !==
                                        opt
                                          ?.maxCounter && (
                                        <Typography
                                          color='textPrimary'
                                          variant='caption'
                                          style={{ marginTop: '1px', marginBottom: '-5px' }}
                                        >
                                          NEXT LVL: +{opt.levels?.find(l => l.level === opt.curLevel + 1)?.points}
                                        </Typography>
                                      )}

                                      <ButtonGroup
                                        color='primary'
                                        size='small'
                                        sx={{
                                          marginTop: '10px'
                                        }}
                                      >
                                        <Button
                                          aria-label='Decrease one level'
                                          disabled={
                                            opt
                                              ?.counter ===
                                            opt
                                              ?.minCounter
                                              ? true
                                              : false
                                          }
                                          onClick={() => {
                                            handleDrecrease(opt.row, opt.col)
                                          }}
                                        >
                                          <RemoveSharp fontSize='small' />
                                        </Button>
                                        <Button
                                          aria-label='Increase one level'
                                          onClick={() => {
                                            handleIncrease(opt.row, opt.col)
                                          }}
                                          disabled={
                                            opt
                                              ?.counter ===
                                            opt
                                              ?.maxCounter
                                              ? true
                                              : false
                                          }
                                        >
                                          <AddSharp fontSize='small' />
                                        </Button>
                                        <Tooltip describeChild title={'Fill skill at max.'}>
                                          <Button
                                            aria-label='increase-all'
                                            onClick={() => {
                                              handleIncrease(opt.row, opt.col, true)
                                            }}
                                            disabled={
                                              opt
                                                ?.counter ===
                                              opt
                                                ?.maxCounter
                                                ? true
                                                : false
                                            }
                                          >
                                            <AddToPhotos />{' '}
                                          </Button>
                                        </Tooltip>
                                        {opt.row !== 'A' && !hideText && (
                                          <Tooltip describeChild title={'Add prerequisites for this skill.'}>
                                            <Button
                                              variant='contained'
                                              aria-label='add-dependencies'
                                              size='small'
                                              color='primary'
                                              disabled={false}
                                              onClick={e => {
                                                handlePrerequisiteFill(opt.row, 1)
                                              }}
                                            >
                                              {<AccountTree fontSize='inherit' />}
                                            </Button>
                                          </Tooltip>
                                        )}
                                      </ButtonGroup>
                                      {opt.row !== 'A' && hideText && (
                                        <ButtonGroup
                                          color='primary'
                                          size='small'
                                          sx={{
                                            marginTop: '5px',
                                            minHeight: '32px',
                                            minWidth: '48px'
                                          }}
                                        >
                                          <Tooltip describeChild title={'Add prerequisites for this skill.'}>
                                            <Button
                                              variant='contained'
                                              aria-label='add-dependencies'
                                              size='small'
                                              color='primary'
                                              disabled={false}
                                              onClick={e => {
                                                handlePrerequisiteFill(opt.row, 1)
                                              }}
                                              sx={{ minHeight: '32px', minWidth: '48px' }}
                                            >
                                              {<AccountTree fontSize='inherit' />}
                                            </Button>
                                          </Tooltip>
                                        </ButtonGroup>
                                      )}
                                    </Box>
                                  </Box>
                                </Paper>
                              </Grid>

                              <Grid size={{ xs: 3, sm: 3 }}key={`${opt.row}-col2_empty`}></Grid>
                            </Grid>

                            <Divider light />
                          </>
                        )
                      } else if (
                        opt.type === 2 &&
                        opt.col === 1 &&
                        (opt.beast === beastInfo.beast || opt.beast === 'all' || opt.beast === 'any')
                      ) {
                        return (
                          <>
                            <Grid container>
                              {/* LEFT */}
                              <Grid size={{ xs: 6, sm: 6, md: 5, lg: 5 }}key={`${opt.row}-col1`} sx={{ padding: '0.5rem' }}>
                                <Paper
                                  elevation={12}
                                  sx={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    justifyContent: 'center',
                                    width: `${hideText ? '150px' : '200px'}`
                                  }}
                                  key={`${opt.row}-col1-paper`}
                                >
                                  <Box
                                    sx={{
                                      margin: theme.spacing(1),
                                      minWidth: 90
                                    }}
                                    key={`${opt.row}-col1-paper-box`}
                                  >
                                    <Typography
                                      variant='body2'
                                      color='primary'
                                      align='center'
                                      sx={{ marginBottom: '10px' }}
                                    >
                                      {beastInfo.options?.find(o => o.row === opt.row && o.col === 1)?.description}
                                    </Typography>

                                    <Box
                                      sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        flexGrow: 'revert',
                                        alignContent: 'center',
                                        justifyContent: 'center',
                                        alignItems: 'center'
                                      }}
                                      key={`${opt.row}-col1-paper-box-sub`}
                                    >
                                      <Badge
                                        color={
                                          beastInfo.options?.find(o => o.row === opt.row && o.col === 1)?.counter ===
                                          beastInfo.options?.find(o => o.row === opt.row && o.col === 1)?.maxCounter
                                            ? 'error'
                                            : 'primary'
                                        }
                                        badgeContent={
                                          beastInfo.options?.find(o => o.row === opt.row && o.col === 1)?.curTotalPoints
                                        }
                                        max={50001}
                                      >
                                        <Avatar
                                          alt={`${opt.row}_${opt.col}`}
                                          src={`/images/beast/row${opt.row}_1_${
                                            beastInfo.options?.find(o => o.row === opt.row && o.col === 1)?.beast ===
                                            'any'
                                              ? beastInfo.beast
                                              : opt.beast
                                          }.webp`}
                                          sx={{
                                            width: theme.spacing(7),
                                            height: theme.spacing(7)
                                          }}
                                        />
                                      </Badge>

                                      <Typography
                                        color={
                                          beastInfo.options?.find(o => o.row === opt.row && o.col === 1)?.counter ===
                                          beastInfo.options?.find(o => o.row === opt.row && o.col === 1)?.maxCounter
                                            ? 'error'
                                            : 'primary'
                                        }
                                        variant='body2'
                                        sx={{
                                          marginTop: '-12px'
                                        }}
                                      >
                                        {beastInfo.options?.find(o => o.row === opt.row && o.col === 1)?.counter} /{' '}
                                        {beastInfo.options?.find(o => o.row === opt.row && o.col === 1)?.maxCounter}{' '}
                                      </Typography>

                                      {/* MAXED */}
                                      {beastInfo.options?.find(o => o.row === opt.row && o.col === 1)?.counter ===
                                        beastInfo.options?.find(o => o.row === opt.row && o.col === 1)?.maxCounter && (
                                        <Typography
                                          color={
                                            beastInfo.options?.find(o => o.row === opt.row && o.col === 1)?.counter ===
                                            beastInfo.options?.find(o => o.row === opt.row && o.col === 1)?.maxCounter
                                              ? 'error'
                                              : 'primary'
                                          }
                                          variant='body2'
                                          style={{ marginTop: '1px', marginBottom: '-5px' }}
                                        >
                                          {beastInfo.options?.find(o => o.row === opt.row && o.col === 1)?.counter ===
                                          beastInfo.options?.find(o => o.row === opt.row && o.col === 1)?.maxCounter
                                            ? '(MAXED)'
                                            : ''}
                                        </Typography>
                                      )}

                                      {/* NEXT LEVEL */}
                                      {beastInfo.options?.find(o => o.row === opt.row && o.col === 1)?.counter !==
                                        beastInfo.options?.find(o => o.row === opt.row && o.col === 1)?.maxCounter && (
                                        <Typography
                                          color='textPrimary'
                                          variant='caption'
                                          style={{ marginTop: '1px', marginBottom: '-5px' }}
                                        >
                                          NEXT LVL: +{opt.levels?.find(l => l.level === opt.counter + 1)?.points}
                                        </Typography>
                                      )}
                                      <ButtonGroup
                                        color='primary'
                                        size='small'
                                        sx={{
                                          marginTop: '10px'
                                        }}
                                      >
                                        <Button
                                          aria-label='Decrease one level'
                                          disabled={
                                            beastInfo.options?.find(o => o.row === opt.row && o.col === 1)?.counter ===
                                            beastInfo.options?.find(o => o.row === opt.row && o.col === 1)?.minCounter
                                              ? true
                                              : false
                                          }
                                          onClick={() => {
                                            handleDrecrease(opt.row, 1)
                                          }}
                                        >
                                          <RemoveSharp fontSize='small' />
                                        </Button>
                                        <Button
                                          aria-label='Increase one level'
                                          onClick={() => {
                                            handleIncrease(opt.row, 1)
                                          }}
                                          disabled={
                                            beastInfo.options?.find(o => o.row === opt.row && o.col === 1)?.counter ===
                                            beastInfo.options?.find(o => o.row === opt.row && o.col === 1)?.maxCounter
                                              ? true
                                              : false
                                          }
                                        >
                                          <AddSharp fontSize='small' />
                                        </Button>
                                        <Tooltip describeChild title={'Fill skill at max.'}>
                                          <Button
                                            aria-label='increase-all'
                                            onClick={() => {
                                              handleIncrease(opt.row, 1, true)
                                            }}
                                            disabled={
                                              beastInfo.options?.find(o => o.row === opt.row && o.col === 1)
                                                ?.counter ===
                                              beastInfo.options?.find(o => o.row === opt.row && o.col === 1)?.maxCounter
                                                ? true
                                                : false
                                            }
                                          >
                                            <AddToPhotos />{' '}
                                          </Button>
                                        </Tooltip>
                                        {!hideText && (
                                          <Tooltip describeChild title={'Add prerequisites for this skill.'}>
                                            <Button
                                              variant='contained'
                                              aria-label='add-dependencies'
                                              size='small'
                                              color='primary'
                                              disabled={false}
                                              onClick={e => {
                                                handlePrerequisiteFill(opt.row, 1)
                                              }}
                                            >
                                              {<AccountTree fontSize='inherit' />}
                                            </Button>
                                          </Tooltip>
                                        )}
                                      </ButtonGroup>
                                      {hideText && (
                                        <ButtonGroup
                                          color='primary'
                                          size='small'
                                          sx={{
                                            marginTop: '5px',
                                            minHeight: '32px',
                                            minWidth: '48px'
                                          }}
                                        >
                                          <Tooltip describeChild title={'Add prerequisites for this skill.'}>
                                            <Button
                                              variant='contained'
                                              aria-label='add-dependencies'
                                              size='small'
                                              color='primary'
                                              disabled={false}
                                              onClick={e => {
                                                handlePrerequisiteFill(opt.row, 1)
                                              }}
                                              sx={{ minHeight: '32px', minWidth: '48px' }}
                                            >
                                              {<AccountTree fontSize='inherit' />}
                                            </Button>
                                          </Tooltip>
                                        </ButtonGroup>
                                      )}
                                    </Box>
                                  </Box>
                                </Paper>
                              </Grid>
                              {/* MIDDLE */}
                              <Grid size={{ md: 2, lg: 2 }}key={`${opt.row}-col2_empty`}></Grid>
                              {/* RIGHT */}
                              <Grid size={{ xs: 6, sm: 6, md: 5, lg: 5 }}key={`${opt.row}-col2`} sx={{ padding: '0.5rem' }}>
                                <Paper
                                  elevation={12}
                                  sx={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    justifyContent: 'center',
                                    width: `${hideText ? '150px' : '200px'}`
                                  }}
                                  key={`${opt.row}-col2-paper`}
                                >
                                  <Box
                                    sx={{
                                      margin: theme.spacing(1),
                                      minWidth: 90
                                    }}
                                    key={`${opt.row}-col2-paper-box`}
                                  >
                                    <Typography
                                      variant='body2'
                                      color='primary'
                                      align='center'
                                      sx={{ marginBottom: '10px' }}
                                    >
                                      {beastInfo.options?.find(o => o.row === opt.row && o.col === 2)?.description}
                                    </Typography>

                                    <Box
                                      sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        flexGrow: 'revert',
                                        alignContent: 'center',
                                        justifyContent: 'center',
                                        alignItems: 'center'
                                      }}
                                      key={`${opt.row}-col2-paper-box-sub`}
                                    >
                                      <Badge
                                        color={
                                          beastInfo.options?.find(o => o.row === opt.row && o.col === 2)?.counter ===
                                          beastInfo.options?.find(o => o.row === opt.row && o.col === 2)?.maxCounter
                                            ? 'error'
                                            : 'primary'
                                        }
                                        badgeContent={
                                          beastInfo.options?.find(o => o.row === opt.row && o.col === 2)?.curTotalPoints
                                        }
                                        max={50001}
                                      >
                                        <Avatar
                                          alt={`${opt.row}_${opt.col}`}
                                          src={`/images/beast/row${opt.row}_2_${
                                            beastInfo.options?.find(o => o.row === opt.row && o.col === 2)?.beast ===
                                            'all'
                                              ? beastInfo.options?.find(o => o.row === opt.row && o.col === 2)?.beast
                                              : beastInfo.beast
                                          }.webp`}
                                          sx={{
                                            width: theme.spacing(7),
                                            height: theme.spacing(7)
                                          }}
                                        />
                                      </Badge>

                                      <Typography
                                        color={
                                          beastInfo.options?.find(o => o.row === opt.row && o.col === 2)?.counter ===
                                          beastInfo.options?.find(o => o.row === opt.row && o.col === 2)?.maxCounter
                                            ? 'error'
                                            : 'primary'
                                        }
                                        variant='body2'
                                        sx={{
                                          marginTop: '-12px'
                                        }}
                                      >
                                        {beastInfo.options?.find(o => o.row === opt.row && o.col === 2)?.counter} /{' '}
                                        {beastInfo.options?.find(o => o.row === opt.row && o.col === 2)?.maxCounter}{' '}
                                      </Typography>

                                      {/* MAXED */}
                                      {beastInfo.options?.find(o => o.row === opt.row && o.col === 2)?.counter ===
                                        beastInfo.options?.find(o => o.row === opt.row && o.col === 2)?.maxCounter && (
                                        <Typography
                                          color={
                                            beastInfo.options?.find(o => o.row === opt.row && o.col === 2)?.counter ===
                                            beastInfo.options?.find(o => o.row === opt.row && o.col === 2)?.maxCounter
                                              ? 'error'
                                              : 'primary'
                                          }
                                          variant='body2'
                                          style={{ marginTop: '1px', marginBottom: '-5px' }}
                                        >
                                          {beastInfo.options?.find(o => o.row === opt.row && o.col === 2)?.counter ===
                                          beastInfo.options?.find(o => o.row === opt.row && o.col === 2)?.maxCounter
                                            ? '(MAXED)'
                                            : ''}
                                        </Typography>
                                      )}

                                      {/* NEXT LEVEL */}
                                      {beastInfo.options?.find(o => o.row === opt.row && o.col === 2)?.counter !==
                                        beastInfo.options?.find(o => o.row === opt.row && o.col === 2)?.maxCounter && (
                                        <Typography
                                          color='textPrimary'
                                          variant='caption'
                                          style={{ marginTop: '1px', marginBottom: '-5px' }}
                                        >
                                          NEXT LVL: +
                                          {
                                            beastInfo.options
                                              ?.find(o => o.row === opt.row && o.col === 2)
                                              .levels?.find(
                                                l =>
                                                  l.level ===
                                                  beastInfo.options?.find(o => o.row === opt.row && o.col === 2)
                                                    .counter +
                                                    1
                                              )?.points
                                          }
                                        </Typography>
                                      )}

                                      <ButtonGroup
                                        color='primary'
                                        size='small'
                                        sx={{
                                          marginTop: '10px'
                                        }}
                                      >
                                        <Button
                                          aria-label='Decrease one level'
                                          disabled={
                                            beastInfo.options?.find(o => o.row === opt.row && o.col === 2)?.counter ===
                                            beastInfo.options?.find(o => o.row === opt.row && o.col === 2)?.minCounter
                                              ? true
                                              : false
                                          }
                                          onClick={() => {
                                            handleDrecrease(opt.row, 2)
                                          }}
                                        >
                                          <RemoveSharp fontSize='small' />
                                        </Button>
                                        <Button
                                          aria-label='Increase one level'
                                          onClick={() => {
                                            handleIncrease(opt.row, 2)
                                          }}
                                          disabled={
                                            beastInfo.options?.find(o => o.row === opt.row && o.col === 2)?.counter ===
                                            beastInfo.options?.find(o => o.row === opt.row && o.col === 2)?.maxCounter
                                              ? true
                                              : false
                                          }
                                        >
                                          <AddSharp fontSize='small' />
                                        </Button>
                                        <Tooltip describeChild title={'Fill skill at max.'}>
                                          <Button
                                            aria-label='increase-all'
                                            onClick={() => {
                                              handleIncrease(opt.row, 2, true)
                                            }}
                                            disabled={
                                              beastInfo.options?.find(o => o.row === opt.row && o.col === 2)
                                                ?.counter ===
                                              beastInfo.options?.find(o => o.row === opt.row && o.col === 2)?.maxCounter
                                                ? true
                                                : false
                                            }
                                          >
                                            <AddToPhotos />{' '}
                                          </Button>
                                        </Tooltip>
                                        {!hideText && (
                                          <Tooltip describeChild title={'Add prerequisites for this skill.'}>
                                            <Button
                                              variant='contained'
                                              aria-label='add-dependencies'
                                              size='small'
                                              color='primary'
                                              disabled={false}
                                              onClick={e => {
                                                handlePrerequisiteFill(opt.row, 2)
                                              }}
                                              sx={{ minHeight: '32px', minWidth: '48px' }}
                                            >
                                              {<AccountTree fontSize='inherit' />}
                                            </Button>
                                          </Tooltip>
                                        )}
                                      </ButtonGroup>
                                      {hideText && (
                                        <ButtonGroup
                                          color='primary'
                                          size='small'
                                          sx={{
                                            marginTop: '5px',
                                            minHeight: '32px',
                                            minWidth: '48px'
                                          }}
                                        >
                                          <Tooltip describeChild title={'Add prerequisites for this skill.'}>
                                            <Button
                                              variant='contained'
                                              aria-label='add-dependencies'
                                              size='small'
                                              color='primary'
                                              disabled={false}
                                              onClick={e => {
                                                handlePrerequisiteFill(opt.row, 2)
                                              }}
                                              sx={{ minHeight: '32px', minWidth: '48px' }}
                                            >
                                              {<AccountTree fontSize='inherit' />}
                                            </Button>
                                          </Tooltip>
                                        </ButtonGroup>
                                      )}
                                    </Box>
                                  </Box>
                                </Paper>
                              </Grid>
                            </Grid>

                            <Divider light />
                          </>
                        )
                      }
                    })}
                  </Box>
                  {totalPoints > 0 && <Divider light>SELECTED TALENTS SKILLS</Divider>}
                  <Box sx={{ display: 'flex', flexDirection: 'column', flexWrap: 'wrap', maxWidth: '450px' }}>
                    {[...beastInfo.options].map(opt => {
                      if (opt.counter > 0) {
                        let statDescription = { text: opt.statText }
                        opt.levels[opt.counter - 1].stat.forEach((s, i) => {
                          const indexText = `$${i + 1}`
                          statDescription.text = statDescription.text?.replace(indexText, s)
                        })

                        return (
                          <List key={`${opt.row}_${opt.col}_${opt.beast}`}>
                            <ListItem>
                              <ListItemAvatar>
                                <Avatar
                                  alt={`${opt.row}_${opt.col}`}
                                  src={`/images/beast/row${opt.row}_${opt.col}_${
                                    opt.beast === 'all' ? 'all' : beastInfo.beast
                                  }.webp`}
                                />
                              </ListItemAvatar>
                              <ListItemText
                                primaryTypographyProps={{ color: 'primary' }}
                                primary={`${opt.description} - Lv${opt.counter}`}
                                secondary={statDescription.text}
                              />
                            </ListItem>
                          </List>
                        )
                      }
                    })}
                  </Box>
                </Paper>
              </Grid>
            </Grid>
          </Box>
          {/*
            La clase la lee HuecoBarraInferior para medir la barra y levantar el pie
            del sitio lo mismo que mide. Sin eso, MUI le da z-index 1100 a este
            AppBar contra el 10 del pie, y la barra lo tapaba entero: los enlaces a
            /privacy/ y /terms/ no se veian ni se podian pulsar en esta pagina.
          */}
          <AppBar
            className='barra-inferior-fija'
            position='fixed'
            color='primary'
            sx={{
              top: 'auto',
              bottom: 0
            }}
          >
            <LinearProgressWithLabel value={progress} />
            <Toolbar>
              <Box sx={{ flexGrow: 1 }} />

              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', alignContent: 'center' }}>
                {/*
                  Los dos con color explicito: van dentro de un AppBar primary y
                  heredaban text.secondary, que sobre #0278AE daba 1,95:1.
                */}
                <Typography variant='caption' sx={{ color: 'common.white', mt: '3px' }}>
                  Available
                </Typography>
                <Typography variant='body1' align='right' sx={{ color: 'common.white' }}>
                  {`${
                    beastInfo.userTalents > 0
                      ? (beastInfo.userTalents - totalPoints).toLocaleString()
                      : (totalAvailablePoints - totalPoints).toLocaleString()
                  }`}
                </Typography>
              </div>

              <Divider
                orientation='vertical'
                flexItem
                sx={{
                  margin: theme.spacing(1)
                }}
              />

              <GreenButton
                variant='contained'
                size='small'
                sx={{
                  margin: theme.spacing(1)
                }}
                onClick={handleFillAll}
                disabled={
                  beastInfo.userTalents > 0
                    ? beastInfo.userTalents - totalPoints <= 0
                      ? true
                      : false
                    : totalAvailablePoints - totalPoints <= 0
                    ? true
                    : false
                }
              >
                FILL
              </GreenButton>
              <RedButton
                variant='contained'
                size='small'
                sx={{
                  margin: theme.spacing(1)
                }}
                onClick={handleClearAll}
              >
                CLEAR
              </RedButton>

              <Divider
                orientation='vertical'
                flexItem
                sx={{
                  margin: theme.spacing(1)
                }}
              />

              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', alignContent: 'center' }}>
                <Typography variant='caption' sx={{ color: 'common.white', mt: '3px' }}>
                  Used / Total
                </Typography>
                <Typography variant='body2' sx={{ color: 'common.white' }}>
                  {totalPoints.toLocaleString()} / {totalAvailablePoints.toLocaleString()}
                </Typography>
                <Typography variant='body2' sx={{ color: 'common.white' }}>
                  ${Math.round((totalPoints / 80) * 5).toLocaleString()}
                </Typography>
              </div>
            </Toolbar>
          </AppBar>
          <HuecoBarraInferior />
        </Card>
      </>
    ),
    skills: (
      <>
        <Card>
          <Box sx={{ flexGrow: 1 }}>
            <Snackbar
              open={snackOpen.open}
              autoHideDuration={snackOpen.time ?? 6000}
              onClose={snackClose}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
              {snackOpen.open && (
                <Alert variant='filled' onClose={snackClose} severity={snackOpen.type}>
                  <AlertTitle>{snackOpen.type === 'success' ? 'Done!' : 'Info:'}</AlertTitle>
                  {snackOpen.open}
                </Alert>
              )}
            </Snackbar>
            <Grid container spacing={2} sx={{ padding: '0.5em' }}>
              <Grid size={{ xs: 12 }}>
                <Paper elevation={12}>
                  <Typography
                    align='center'
                    variant='h6' component='p'
                    color='primary'
                    sx={{ paddingTop: '0.5em', paddingBottom: '0.5em' }}
                  >
                    BEAST PASSIVE SKILLS SIMULATOR
                  </Typography>
                  <Divider>{<Typography variant='caption'>SETUP</Typography>}</Divider>
                  <Box
                    sx={{
                      padding: '0.8em',
                      display: 'flex',
                      flexDirection: 'column'
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        flexWrap: 'wrap',
                        justifyContent: 'center',
                        alignItems: 'center'
                      }}
                    >
                      <TextField
                        align='center'
                        id='beast-skill-template-select'
                        size='small'
                        variant='outlined'
                        label='Presets'
                        select
                        sx={{ minWidth: '200px', mt: 3 }}
                        value={skillTemplate}
                        onChange={handleBeastSkillChangeTemplate}
                      >
                        {beastSkillsTemplates?.map(option => (
                          <MenuItem key={option.k} value={option.k}>
                            {option.desc}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Box>
                  </Box>
                  <Divider>{<Typography variant='caption'>SKILLS SLOTS</Typography>}</Divider>
                  <Box sx={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap' }}>
                    {slotLevel?.map((slot, index) => {
                      return (
                        <>
                          <Grid size={{ xs: 12, sm: 12, md: 3, lg: 3 }} sx={{ padding: '1.5em' }}>
                            <Paper
                              elevation={12}
                              sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'center',
                                alignItems: 'center'
                              }}
                            >
                              <Avatar
                                alt={slot.key}
                                src={`/images/beast_skills/${slot.skill?.key}.webp`}
                                sx={{
                                  width: theme.spacing(7),
                                  height: theme.spacing(7),
                                  marginTop: '10px'
                                }}
                              >
                                {slot?.key === 1 && <LooksOne />}
                                {slot?.key === 2 && <LooksTwo />}
                                {slot?.key === 3 && <Looks3 />}
                                {slot?.key === 4 && <Looks4 />}
                              </Avatar>
                              <Divider flexItem light variant='middle' sx={{ margin: '5px' }} />
                              <Box>
                                <TextField
                                  align='center'
                                  select
                                  size='small'
                                  label={`SLOT #${slot.key}`}
                                  id={'slot-select-' + slot.key}
                                  variant='outlined'
                                  type='info'
                                  helperText=''
                                  value={slotLevel[index].skill?.key ?? ''}
                                  sx={{ margin: theme.spacing(1), width: '15ch' }}
                                  onChange={e => {
                                    handleBeastSkillChange(e, slot.key)
                                  }}
                                >
                                  <ListSubheader>WAR</ListSubheader>

                                  {beastSkillList
                                    ?.filter(f => f.group === 'WAR')
                                    ?.map(option => (
                                      <MenuItem key={option.key} value={option.key}>
                                        {option.desc}
                                      </MenuItem>
                                    ))}
                                  <ListSubheader>Development</ListSubheader>
                                  {beastSkillList
                                    ?.filter(f => f.group === 'Development')
                                    ?.map(option => (
                                      <MenuItem key={option.key} value={option.key}>
                                        {option.desc}
                                      </MenuItem>
                                    ))}
                                  <ListSubheader>Misc</ListSubheader>
                                  {beastSkillList
                                    ?.filter(f => f.group === 'Misc')
                                    ?.map(option => (
                                      <MenuItem key={option.key} value={option.key}>
                                        {option.desc}
                                      </MenuItem>
                                    ))}
                                </TextField>
                                <Divider flexItem light />
                                <Typography variant='body1' color='primary' align='center'>
                                  Level:
                                </Typography>
                                <Typography variant='h5' component='p' color='secondary' align='center'>
                                  {slot.level}
                                </Typography>
                              </Box>
                              <Divider flexItem light variant='middle' sx={{ margin: '5px' }} />

                              <ButtonGroup color='primary' size='small' sx={{ mt: 1, mb: 3 }}>
                                <Tooltip describeChild title='Decrease 1 level.'>
                                  <Button
                                    aria-label='Decrease one level'
                                    color='secondary'
                                    disabled={slot?.skill?.key?.length > 0 ? false : true}
                                    onClick={e => {
                                      handleDrecreaseBeastSkillChange(slot.key)
                                    }}
                                  >
                                    <RemoveSharp fontSize='small' />
                                  </Button>
                                </Tooltip>
                                <Tooltip describeChild title='Increase 1 level.'>
                                  <Button
                                    aria-label='Increase one level'
                                    disabled={slot?.skill?.key?.length > 0 ? false : true}
                                    onClick={e => {
                                      handleIncreaseBeastSkillChange(slot.key)
                                    }}
                                  >
                                    <AddSharp fontSize='small' />
                                  </Button>
                                </Tooltip>
                                <Tooltip describeChild title='Set Max Level'>
                                  <Button
                                    disabled={slot?.skill?.key?.length > 0 ? false : true}
                                    onClick={e => {
                                      handleIncreaseBeastSkillAll(slot.key, 30)
                                    }}
                                  >
                                    <LibraryAdd fontSize='small' />
                                  </Button>
                                </Tooltip>
                              </ButtonGroup>
                            </Paper>
                          </Grid>
                        </>
                      )
                    })}
                  </Box>
                  {(slotLevel[0].skill?.key?.length > 0 ||
                    slotLevel[1].skill?.key?.length > 0 ||
                    slotLevel[2].skill?.key?.length > 0 ||
                    slotLevel[3].skill?.key?.length > 0) && (
                    <Divider>{<Typography variant='caption'>SELECTED SKILLS</Typography>}</Divider>
                  )}
                  <Box>
                    {slotLevel?.map(opt => {
                      let statDescription = { text: opt?.skill?.info }

                      const newStat =
                        opt?.level === 1
                          ? opt?.skill?.statInit
                          : opt?.skill?.statInit + opt?.skill?.statInc * (opt?.level - 1)
                      statDescription.text = statDescription.text?.replace('$1', newStat.toFixed(1))

                      if (opt?.skill?.key?.length > 0) {
                        return (
                          <List key={opt.key}>
                            <ListItem>
                              <ListItemAvatar>
                                <Avatar
                                  alt={`${opt.key}`}
                                  src={`/images/beast_skills/${opt?.skill?.key}.webp`}
                                  sx={{
                                    width: theme.spacing(5),
                                    height: theme.spacing(5)
                                  }}
                                />
                              </ListItemAvatar>
                              <ListItemText
                                primaryTypographyProps={{ color: 'primary' }}
                                primary={`Skill #${opt?.key} - ${opt?.skill?.desc} - Lv${opt?.level}`}
                                secondary={`${statDescription.text}  Requirements: ${
                                  opt?.requeriments
                                } Total Experience needed: ${opt.expNeeded.toLocaleString()}. Total Scrolls needed from Lv1: ${Math.round(
                                  opt.expNeeded / 50
                                ).toLocaleString()}. ${
                                  opt?.level < 30
                                    ? `Scrolls needed for next level (${
                                        opt?.level !== 30 ? opt?.level + 1 : '30'
                                      }): ${Math.round(
                                        beastSkills?.skillLevel?.find(
                                          lvl => lvl.level === (opt?.level !== 30 ? opt?.level + 1 : 30)
                                        ).exp / 50
                                      ).toLocaleString()}`
                                    : ''
                                } `}
                              />
                            </ListItem>
                          </List>
                        )
                      }
                    })}
                  </Box>
                </Paper>
              </Grid>
            </Grid>
          </Box>
        </Card>
      </>
    )
  }

  return (
    <>
      <Grid container spacing={6}>
        <Grid size={{ xs: 12 }}>
          <Card
            sx={{
              padding: '2rem',
              borderRadius: '12px',
              boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)',
              borderTop: '6px solid',
              borderTopColor: 'primary.main'
            }}
          >
            <CustomHeader icon='beast' title='BEAST' />
            <Divider sx={{ marginY: '1rem' }} />
            <Typography variant='h6' component='h2' sx={{ fontWeight: 'bold', marginBottom: '1rem', color: 'primary.main' }}>
              Beast Configuration
            </Typography>
            <Typography variant='body2' sx={{ color: 'text.secondary', mb: 1 }}>
              The Beast is a powerful combat unit in War and Order that requires careful configuration to reach its full potential. Choosing the right beast type and assigning the correct abilities can significantly shift the outcome of battles.
            </Typography>
            <Typography variant='body2' sx={{ color: 'text.secondary' }}>
              Use this tool to configure beast skills, levels, and formation positioning, then save your setups directly in the browser for quick switching between different combat strategies.
            </Typography>
          </Card>
        </Grid>

        {activeTab === undefined ? null : (
          <Grid size={{ xs: 12 }}>
            <TabContext value={activeTab}>
              <Grid container spacing={6}>
                <Grid size={{ xs: 12 }}>
                  <TabList
                    variant='scrollable'
                    scrollButtons='auto'
                    onChange={handleChange}
                    aria-label='customized tabs'
                  >
                    <Tab
                      aria-label='Talents'
                      value='talents'
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', ...(!hideText && { '& svg': { mr: 2 } }) }}>
                          <Icon fontSize={20} icon='game-icons:black-book' />
                          {'Talents'}
                        </Box>
                      }
                    />
                    <Tab
                      aria-label='Skills'
                      value='skills'
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', ...(!hideText && { '& svg': { mr: 2 } }) }}>
                          <Icon fontSize={20} icon='game-icons:book-aura' />
                          {'Skills'}
                        </Box>
                      }
                    />
                  </TabList>
                </Grid>
                <Grid size={{ xs: 12 }} sx={{ pt: theme => `${theme.spacing(4)} !important` }}>
                  {isLoading ? (
                    <Box sx={{ mt: 6, display: 'flex', alignItems: 'center', flexDirection: 'column' }}>
                      <CircularProgress sx={{ mb: 4 }} />
                      <Typography>Loading...</Typography>
                    </Box>
                  ) : (
                    /*
                      Un solo TabPanel con value={activeTab} deja en el DOM nada mas que la
                      pestaña activa, y la de esta pagina arranca en un simulador. Resultado:
                      los 28 beast skill options de la pestaña 'skills' no existian en el HTML exportado.
                    
                      Se probo con keepMounted y se descarto: la pestaña son selectores e imagenes, no dato tabular: montarla sumaba 38 palabras y bastante DOM.
                      Se deja el mapeo por clave porque es la forma correcta del componente,
                      pero sin montar nada de mas.
                    */
                    Object.keys(tabContentList).map(clave => (
                      <TabPanel key={clave} sx={{ p: 0 }} value={clave} keepMounted={false}>
                        {tabContentList[clave]}
                      </TabPanel>
                    ))
                  )}
                </Grid>
              </Grid>
            </TabContext>
          </Grid>
        )}
      </Grid>
        <Grid size={{ xs: 12 }} sx={{ pt: 6 }}>
          <TablaReferencia
            titulo='The 28 beast talents: which beasts get them, max level, total cost and what they do'
            columnas={COLUMNAS_BEAST}
            filas={FILAS_BEAST}
            idFila='id'
          />
        </Grid>
        <Grid size={{ xs: 12 }} sx={{ pt: 6 }}>
          <TablaReferencia
            titulo='Talent points and value at every level'
            columnas={COLUMNAS_BEAST_NIVEL}
            filas={FILAS_BEAST_NIVEL}
            idFila='id'
          />
        </Grid>
    </>
  )
}

Beast.noLoadingGate = true

export default Beast
